import { useCallback, useMemo, useState, type MouseEvent } from "react";

import type { Token } from "../api/types";
import { useTimeline } from "../hooks/useTimeline";
import { useTimelineOrders } from "../hooks/useTimelineOrders";
import {
  EDGE_H,
  FLOW_H,
  GAP_H,
  INDEX_H,
  marksNear,
  PLOT_W,
  PRICE_H,
  timelineHover,
  timelineView,
  type OutcomeNames,
  type TimelineChart,
  type TimelineHover,
  type TimelineLine,
  type TimelineMark,
  type TimelineTick,
} from "../lib/timeline";
import { useParticipantPicks } from "../store/useParticipantPicks";
import { useParticipantTags } from "../store/useParticipantTags";
import { Empty, Note, Panel } from "./Panel";

/**
 * Ширина жёлоба шкалы значений, пиксели.
 *
 * Восемь знаков моноширинного текста в 10 пикселей: «75 641.7» — самая длинная
 * подпись шкалы индекса; у прочих полотен («2 160», «0.604», «-160.0») их шесть.
 * Жёлоб один на все полотна, иначе их левые края разъехались бы. Фиксированный, а
 * не по содержимому, чтобы холст не ездил при смене чисел: график, который
 * дёргается по горизонтали, читается не лучше того, что дёргался по вертикали.
 */
const Y_GUTTER = 52;

interface Props {
  conditionId: string;
  tokens: readonly Token[];
}

/**
 * Панель «Ход накопления».
 *
 * # Почему inline-SVG, а не библиотека графиков
 *
 * Тот же довод, что у гистограммы счёта (PnLShape): здесь два полотна, четыре
 * линии и две подписи на ось. Тянуть под это графическую библиотеку значило бы
 * добавить сотни килобайт к бандлу ради того, что рисуется четырьмя `<path>`.
 *
 * # Почему полотна ДВА, а не две шкалы на одном
 *
 * Доллары и вероятность несоизмеримы, и общая рамка предлагала бы читать
 * пересечение линии цены с линией накопления как событие. События там нет: это
 * пересечение двух произвольно выбранных масштабов.
 *
 * Ось X при этом ОДНА и считается один раз (lib/timeline.ts): посчитай её на
 * каждое полотно отдельно — и они разъехались бы на любом расхождении входов, а
 * разъезд такой глазом не виден и превращает панель в генератор ложных выводов
 * о том, что чему предшествовало.
 */
export function TimelinePanel({ conditionId, tokens }: Props) {
  // Фильтр — ТОТ ЖЕ, что режет таблицу накопления, и читается из тех же сторов.
  // Своего переключателя у панели нет намеренно: два фильтра на одном экране
  // означали бы, что график и таблица под ним описывают разные наборы
  // кошельков, а подписаны оба словом «накоплено».
  const tagInclude = useParticipantTags((s) => s.include);
  const tagExclude = useParticipantTags((s) => s.exclude);
  const groupInclude = useParticipantTags((s) => s.groupInclude);
  const groupExclude = useParticipantTags((s) => s.groupExclude);
  const picks = useParticipantPicks((s) => s.picked);

  // Отметки уезжают списком и СИЛЬНЕЕ фильтров — тот же порядок, что у строки
  // «Итого»: отмечено — считаем по отмеченным, не отмечено — по фильтру.
  // Сортировка тут же: множество отдаёт адреса в порядке вставки, и та же
  // выборка дала бы два разных ключа запроса.
  const filter = useMemo(
    () => ({
      tags: tagInclude,
      notags: tagExclude,
      groups: groupInclude,
      notgroups: groupExclude,
      wallets: [...picks].sort(),
    }),
    [tagInclude, tagExclude, groupInclude, groupExclude, picks],
  );
  const q = useTimeline(conditionId, filter);

  // Имена исходов подставляет ИНТЕРФЕЙС из справочника рынка — тот же приём и
  // тот же довод, что у сводки групп: вторая копия сопоставления «токен →
  // исход» в ответе означала бы два места, где токен получает имя.
  const outcomes: OutcomeNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const t of tokens) out[t.asset_id] = t.outcome;
    return out;
  }, [tokens]);

  const orders = useTimelineOrders(conditionId);
  const view = useMemo(
    () => timelineView(q.data, outcomes, orders),
    [q.data, outcomes, orders],
  );

  /**
   * Секунда окна под курсором. null — курсора на холсте нет.
   *
   * Состояние ЗДЕСЬ, а не в каждом полотне: ось у них общая, и перекрестие
   * обязано стоять в обоих на одной вертикали. Два независимых состояния
   * разъезжались бы ровно в тот момент, когда оператор сравнивает цену с
   * накоплением, — то есть всегда.
   */
  const [hoverAt, setHoverAt] = useState<number | null>(null);
  const hover = useMemo(() => timelineHover(q.data, hoverAt), [q.data, hoverAt]);

  // Пиксель под курсором в секунду окна: холст тянется по ширине карточки, и
  // пересчёт возможен только здесь, где известна его фактическая ширина.
  const onMove = useCallback(
    (event: { currentTarget: Element; clientX: number }) => {
      const box = event.currentTarget.getBoundingClientRect();
      if (box.width <= 0) return;
      const share = (event.clientX - box.left) / box.width;
      // Левый край оси — предстарт, поэтому доля ширины откладывается от него,
      // а не от открытия окна.
      setHoverAt(view.from + Math.max(0, Math.min(1, share)) * view.span);
    },
    [view.from, view.span],
  );
  const onLeave = useCallback(() => setHoverAt(null), []);

  return (
    <Panel
      title="Ход накопления"
      subtitle={view.basis[0] || undefined}
      actions={
        <span className="flex items-baseline gap-3">
          {/* Состав НЕ выбирается здесь: его задаёт фильтр таблицы накопления.
              Здесь он только называется — чтобы, глядя на линию, было видно,
              чей это набор, не прокручивая экран до чипов фильтра. */}
          <span className="text-xs text-[var(--color-muted)]">{view.picked}</span>
          {/* Пометка стоит ПОСТОЯННО и лишь прячется через visibility — тот же
              приём, что в шапке накопления: появляющееся слово сдвигало бы
              кнопки левее на свою ширину при каждом опросе. */}
          <span
            className={`text-xs text-[var(--color-muted)]${q.isFetching ? "" : " invisible"}`}
            title="запрос ряда в пути"
            aria-hidden={!q.isFetching}
          >
            обновляем…
          </span>
        </span>
      }
    >
      {view.empty && <Empty>{view.empty}</Empty>}
      {!view.empty && (
        <>
          {/* Основания ПЕРЕД картинкой, а не под ней: «линия ровная» на трёх
              сделках и на трёх тысячах — разные утверждения, и по картинке они
              неразличимы. */}
          <p className="text-xs text-[var(--color-muted)]">{view.basis.join(" · ")}</p>

          <Plot
            title="Цена: стакан и средняя цена входа"
            chart={view.price}
            height={PRICE_H}
            ticks={view.xTicks}
            openAt={view.openAt}
            hover={hover}
            hoverX={hoverX(hoverAt, view.from, view.span)}
            onMove={onMove}
            onLeave={onLeave}
          />
          {/* Индекс — СРАЗУ под ценой стакана: стакан переоценивается вслед за
              ним, и сравнивают их по одной вертикали. До открытия на месте
              страйка стоит его прогноз — отдельной пунктирной линией, как крышка
              над K на карточке. */}
          <Plot
            title="Индекс: Binance, сырой Chainlink, S, наш отчёт Ŝ и страйк — по секунде, когда их знала карточка"
            chart={view.index}
            height={INDEX_H}
            ticks={view.xTicks}
            openAt={view.openAt}
            hover={hover}
            hoverX={hoverX(hoverAt, view.from, view.span)}
            onMove={onMove}
            onLeave={onLeave}
          />
          <Plot
            title="Отклонение от страйка и запас, $"
            chart={view.gap}
            height={GAP_H}
            ticks={view.xTicks}
            openAt={view.openAt}
            zeroAt={view.gap.zeroAt}
            hover={hover}
            hoverX={hoverX(hoverAt, view.from, view.span)}
            onMove={onMove}
            onLeave={onLeave}
          />
          <Plot
            title={
              view.edge.lines.some((l) => l.key === "all-taker")
                ? "Накоплено нетто, share — тейкеры против мейкеров рынка"
                : "Накоплено нетто, share — исход × роль, со знаком к первому исходу"
            }
            chart={view.edge}
            height={EDGE_H}
            ticks={view.xTicks}
            openAt={view.openAt}
            zeroAt={view.edge.zeroAt}
            hover={hover}
            hoverX={hoverX(hoverAt, view.from, view.span)}
            onMove={onMove}
            onLeave={onLeave}
          />
          {/* Ставки — ТРЕТЬИМ полотном, а не поверх накопления: накопленное это
              тысячи шеров, поток за секунду — десятки, и на одной шкале столбики
              легли бы в линию у нуля именно там, где интересны. Общая ось
              времени держит всплеск в одной вертикали с изломом линии выше. */}
          <Plot
            title={
              view.flow.zeroAt === FLOW_H
                ? "Оборот за корзину, share — когда затишье, а когда поток"
                : "Ставки за корзину, share — когда, куда и какого объёма"
            }
            chart={view.flow}
            height={FLOW_H}
            ticks={view.xTicks}
            openAt={view.openAt}
            zeroAt={view.flow.zeroAt}
            hover={hover}
            hoverX={hoverX(hoverAt, view.from, view.span)}
            onMove={onMove}
            onLeave={onLeave}
          />

          {/* Ось времени — общая, и подписана она ОДИН раз под обоими
              полотнами: две подписи под одной осью читались бы как две оси.

              Крайние подписи прижаты к краям, а не центрированы: центрированная
              подпись на границе холста уезжала бы половиной за край — замер до
              правки показал «+24 c», перенесённое на две строки. */}
          <div className="relative mt-1 h-4" style={{ marginLeft: `${Y_GUTTER}px` }}>
            {view.xTicks.filter((tick) => !tick.minor).map((tick) => (
              <span
                key={`${tick.at}-${tick.label}`}
                className={`absolute text-[10px] text-[var(--color-muted)]${
                  tick.anchor === "middle" ? " -translate-x-1/2" : ""
                }`}
                style={
                  tick.anchor === "end"
                    ? { right: 0 }
                    : { left: `${(tick.at / PLOT_W) * 100}%` }
                }
              >
                {tick.label}
              </span>
            ))}
          </div>

          {/* Приписка курсора стоит В ЗАРЕЗЕРВИРОВАННОЙ строке, а не появляется
              вместе с ним: появляющаяся строка толкала бы вниз всё, что под
              графиком, при каждом заходе курсора на холст. Без курсора здесь
              стоит досделочное накопление — тоже одна строка. */}
          <p className="mt-2 min-h-4 text-xs text-[var(--color-muted)]">
            {hover ? `${hover.at} · ${hover.note}` : view.beforeOpen}
          </p>

          {/* Строка «что первично» — главный ответ панели, и стоит она отдельно
              от оснований, а не в их ряду: у неё своя оговорка про предел
              разрешения, и слитая с ними она читалась бы как ещё одно число. */}
          <p className="mt-2 text-xs" title={view.leadHint}>
            <span className="text-[var(--color-muted)]">что первично: </span>
            <span>{view.lead}</span>
          </p>

          {view.alarms.map((text) => (
            <p key={text} className="mt-1 text-xs text-[var(--color-warn)]">
              {text}
            </p>
          ))}

          <Note>
            <p>
              Внизу — сколько набрали ОТОБРАННЫЕ кошельки нетто, в шерах, нарастающим итогом
              от левого края оси: куплено минус продано по первому исходу минус то же
              по второму. Это ровно то число, что стоит в строке «Итого» над
              таблицей накопления, только во времени: знак — сторона, модуль —
              размер позиции. Шеры, а не доллары, потому что на дешёвой стороне
              они расходятся на порядок: тысяча шеров по 0.05 — это $50
              вложенных и $1000 на погашении. Доллары показывает курсор.
            </p>
            <p className="mt-2">
              ИТОГА по рынку целиком на этом полотне нет, и это не пропуск: он
              тождественно ноль. Шер Up существует только в паре с шером Down —
              комплект чеканится из доллара и разрезается, — поэтому сумма по
              всем кошелькам всегда нулевая (замер на восьми живых окнах: ноль
              при обороте в тысячи шеров). Роль второй линии играет нулевая
              черта: у отобранных +500 значит у всех остальных ровно −500.
            </p>
            <p className="mt-2">
              БЕЗ ФИЛЬТРА полотно всё равно работает: в ноль сходится СУММА, а не
              составляющие. Рисуются две линии — тейкеры и мейкеры рынка,
              зеркальные друг другу: агрессивные деньги давят в одну сторону,
              пассивные их принимают. Разрез по РОЛИ, а не по исходу × роль,
              потому что по рынку целиком исход упирается в чеканку: замер на
              живом окне дал ±37…40 тысяч шеров по исходам при разнице около
              тысячи — четыре линии такого масштаба показали бы объём чеканки,
              спрятав сигнал в толщину линии. По роли: тейкеры −1089, мейкеры
              +1100.
            </p>
            <p className="mt-2">
              Итог разложен на ЧЕТЫРЕ составляющие — исход × роль, тот же разрез,
              что колонки «Тейкером» и «Мейкером» в таблице. Цвет несёт исход,
              штрих — роль: сплошная тейкером, пунктир мейкером. Четыре линии в
              сумме дают итог ровно, и это сторожится тестом.
            </p>
            <p className="mt-2">
              Составляющие второго исхода нарисованы НИЖЕ НУЛЯ, хотя в таблице то
              же число стоит положительным. Причина в оси: полотно меряет перевес
              к первому исходу, и набранный второй тянет его вниз. Та же позиция,
              другая ось.
            </p>
            <p className="mt-2">
              Роль берётся из строки ленты. Строка без флага агрессора считается
              мейкерской — это не умолчание, а следствие: не подтверждённый
              агрессор им и не является. Когда источник меняет поведение, таблица
              над графиком говорит об этом отдельной строкой, и разрез по роли
              тогда неверен у обоих.
            </p>
            <p className="mt-2">
              Знак цветом НЕ кодируется — набранное не есть прибыль; сторону
              читают по положению относительно нулевой черты.
            </p>
            <p className="mt-2">
              Сплошные линии вверху — середина стакана, а не цена состоявшихся
              сделок. Это не педантизм: цена принтов частично сделана той самой
              стороной, чьё накопление нарисовано ниже, и вопрос «кто первичен»
              получил бы по ней тавтологичный ответ.
            </p>
            <p className="mt-2">
              Пунктирные линии вверху — СРЕДНЯЯ ЦЕНА ВХОДА, приведённая к оси
              первого исхода: купить второй за 0.2 значит заплатить 0.8 за
              первый, поэтому обе покупки складываются с весом в шерах. Она стоит
              на одной шкале со стаканом, и в этом весь смысл полотна: видно,
              дороже или дешевле нынешней котировки набирала сторона. Продажи в
              неё не входят — вопрос «по чём набирали» про вход. У линии всего
              рынка своя оговорка: чеканка комплекта записывается как две покупки
              по p и 1−p, то есть тянет её к 0.5.
            </p>
            <p className="mt-2">
              Точки на полотне цены — НАШИ ордера этого окна: сплошная —
              настоящий, кольцо — пробный; цвет — исход, на линию стакана
              которого точка ложится. Стоит она в секунду постановки и на ЛИМИТЕ
              ордера, а не на цене исполнения: её у настоящего ордера биржа не
              отдаёт, а проба на своей средней налива легла бы не там, где
              настоящий ордер той же кнопки. Бледная точка — ордер закрыт, ничего
              не купив. Курсор у точки раскрывает ордер целиком: тип, размер,
              исполнение, статус и причину отказа, у пробы — ещё среднюю налива и
              стакан с его возрастом. Ордер за краем оси к краю не прижимается —
              их число стоит в легенде.
            </p>
            <p className="mt-2">
              Нижнее полотно — САМИ СТАВКИ за корзину, а не накопленное: по нему
              видно, когда пришла крупная и какого объёма, и какой отрезок прошёл
              вовсе без ставок. Отдельным полотном, потому что величины
              несоизмеримы: накопленное — тысячи шеров, поток за секунду —
              десятки, и на одной шкале столбики легли бы в линию у нуля именно
              там, где интересны. Ось времени общая, так что всплеск стоит ровно
              под своим изломом линии выше.
            </p>
            <p className="mt-2">
              БЕЗ ФИЛЬТРА нижнее полотно показывает оборот одним столбиком вверх
              и без направления. Направления по рынку целиком не существует: в
              журнале лежат ОБЕ ноги каждой сделки, и стороны гасят друг друга —
              сверка на живом окне дала 3425 сошедшихся транзакций из 3432.
              Остаточный перекос секунды это не поток, а артефакт границы: у 354
              транзакций из 3432 ноги попали в разные секунды. Нарисуй его двумя
              цветными столбиками — и панель назвала бы направлением разъезд
              меток времени. Оборот при этом честен, и двойной счёт ног в нём
              назван подписью.
            </p>
            <p className="mt-2">
              С ФИЛЬТРОМ стороны идут РАЗНЫМИ столбиками вверх и вниз, а не одним со знаком:
              встречная пара в ту же секунду погасила бы обе, и крупная ставка
              исчезла бы с графика ровно в тот момент, ради которого он и
              заведён. Шкала симметрична и задана максимальным столбиком окна —
              крупная обязана быть выше всех, а не сплющена к соседям.
            </p>
            <p className="mt-2">
              Курсор на холсте показывает значения РОВНО В ЭТОЙ СЕКУНДЕ — в
              легенде вместо последних, а под графиком строкой с деньгами, числом
              сделок и возрастом стакана. Там же — крупнейшая ОДИНОЧНАЯ ставка
              корзины: она отличает «одну крупную» от «многих мелких», а по
              высоте столбика они неразличимы. По оси секунду считать не нужно:
              подписи стоят через 15 секунд, точное значение даёт курсор.
            </p>
            <p className="mt-2">
              Ось начинается за ПОЛТОРЫ МИНУТЫ до открытия окна, а открытие отмечено
              пунктиром. Слева от него котировка живёт ещё без страйка: страйк —
              это TWAP по секундам перед открытием, и стакан переоценивается вслед
              за ним примерно с минуты до старта. До начала окна страйка ход цены на
              исход не влияет вовсе — он сдвигает страйк и итог одинаково, — поэтому
              ранняя часть предстарта показывает, как стакан вёл себя до переоценки.
              Всё набранное раньше левого края уже стоит в первой точке линии, а
              итог всего предстарта — строкой под графиком. Котировки на проде
              начинаются позже края: подписка на следующее окно открывается за
              минуту до старта.
            </p>
            <p className="mt-2">
              Полотна ИНДЕКСА — то, что в каждую секунду показывала карточка рынка:
              опубликованный S, наш отчёт Ŝ t−3 (источник его ещё не опубликовал),
              тот же отчёт по ряду, достроенному спотом, и страйк. Линии положены по
              секунде, когда число было известно, а не по метке отчёта: по меткам Ŝ
              и S совпали бы, а сдвиг между ними и есть наша фора. Числа
              пересчитываются по журналу индекса теми же функциями, что считают
              карточку, поэтому последняя точка обязана совпадать с ней.
            </p>
            <p className="mt-2">
              Там же — сырой ряд Chainlink (серый), из которого S усредняется за
              минуту, и спот Binance (фиолетовый), который приходит по тому же
              проводу примерно на секунду раньше сырого. Стакан переоценивается по
              ним, а не по S: рывок биржи S доносит наклоном на полминуты позже,
              поэтому скачок цены ищут по ступеньке этих двух линий. Binance
              котирован в USDT и сдвинут на базис окна одним числом (оно в
              легенде): ход линии точный, а уровень против страйка — с точностью
              до дрейфа базиса. Под курсором биржа — в своей валюте, без сдвига.
              Линии биржи нет, когда трейдер не слушает спот.
            </p>
            <p className="mt-2">
              До открытия на месте страйка стоит его ПРОГНОЗ K̂ — пунктиром: страйк
              это TWAP по секундам перед открытием, и пока они идут, число
              двигается. После открытия его сменяет записанный K сплошной линией.
              Ниже — отклонение S − K (где индекс сейчас) и запас (где кончится окно,
              если цена замрёт). До начала окна страйка запас лежит ровно на нуле:
              ход цены сдвигает страйк и итог одинаково, и стороны у окна ещё нет. С
              началом окна страйка запас отходит от нуля — и ровно тогда начинает
              переоцениваться стакан.
            </p>
            <p className="mt-2">
              Пропуск в линии цены — это отсутствие образца, а не спокойный
              рынок: между образцами цена неизвестна, и соединять их значило бы
              рисовать движение, которого никто не наблюдал. У накопления
              наоборот: тихая секунда — ровная линия, потому что между сделками
              набранное не меняется.
            </p>
            <p className="mt-2">
              Состав задаёт ФИЛЬТР ТАБЛИЦЫ под графиком — теги, подгруппы и
              галочки на строках. Отдельного переключателя у панели нет
              намеренно: два фильтра на одном экране означали бы, что график и
              таблица описывают разные наборы кошельков, а подписаны оба словом
              «накоплено». Отличие одно и названо в шапке: ряд считается по ВСЕМ
              подходящим кошелькам снимка, а таблица показывает сотню строк.
            </p>
            <p className="mt-2">
              Подгруппа при этом назначена кошельку за ВСЮ накопленную глубину, а
              линия рисует накопление за ЭТО окно. Два разных окна на одном
              экране, и смешать их — главный способ прочитать панель неверно.
            </p>
          </Note>
        </>
      )}
    </Panel>
  );
}

/**
 * Одно полотно.
 *
 * `preserveAspectRatio="none"` — полотно тянется по ширине карточки, а высота
 * задана в тех же единицах, что и координаты линий: иначе при узкой колонке
 * график сжимался бы вместе с высотой и наклон линии переставал бы что-либо
 * означать.
 */
function Plot({
  title,
  chart,
  height,
  ticks,
  openAt,
  zeroAt,
  hover,
  hoverX,
  onMove,
  onLeave,
}: {
  title: string;
  chart: TimelineChart;
  height: number;
  /** Деления оси ВРЕМЕНИ — общие у обоих полотен, приходят сверху. */
  ticks: TimelineTick[];
  /** Координата открытия окна. null — предстарта на оси нет. */
  openAt: number | null;
  zeroAt?: number | undefined;
  /** Значения под курсором. null — курсора на холсте нет. */
  hover: TimelineHover | null;
  /** Координата перекрестия в единицах viewBox. null — не рисовать. */
  hoverX: number | null;
  onMove: (event: { currentTarget: Element; clientX: number }) => void;
  onLeave: () => void;
}) {
  const marks = chart.marks ?? [];
  // Ключи, а не сами точки: ответ опроса пересобирает точки, и подсказка,
  // державшая прежние объекты, показывала бы статус ордера на момент наведения
  // — «LIVE» у ордера, который уже исполнился.
  const [nearKeys, setNearKeys] = useState<string[]>([]);
  const near = nearKeys
    .map((key) => marks.find((m) => m.key === key))
    .filter((m): m is TimelineMark => m !== undefined);

  const move = (event: MouseEvent<HTMLDivElement>) => {
    onMove(event);
    if (marks.length === 0) return;
    const box = event.currentTarget.getBoundingClientRect();
    const keys = marksNear(marks, event.clientX - box.left, event.clientY - box.top, box.width).map(
      (m) => m.key,
    );
    setNearKeys((prev) => (prev.join("|") === keys.join("|") ? prev : keys));
  };
  const leave = () => {
    onLeave();
    setNearKeys([]);
  };

  return (
    <div className="mt-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-[var(--color-muted)]">{title}</span>
        <Legend lines={chart.lines} hover={hover} />
      </div>
      {/* Шкала значений — в СОБСТВЕННОМ жёлобе слева, а не подписями поверх
          холста у правого края. Плавающая подпись читается как часть графика, а
          не как ось: оператор сказал «оси Y нет», глядя ровно на неё. Жёлоб
          фиксированной ширины ещё и держит левый край всех полотен на одной
          вертикали — иначе полотна с разными числами разъезжались бы. */}
      {chart.empty ? (
        // Причина занимает РОВНО ту же высоту, что и полотно, и это не
        // выравнивание ради красоты. Схлопнись пустое полотно в строку текста —
        // карточка стала бы на сотню пикселей ниже, и всё, что под ней, дёрнулось
        // бы в момент, когда журнал котировки начал писать. Ровно та беда, из-за
        // которой панель уехала выше сетки: график, который прыгает, не читается.
        <div
          className="mt-1 flex items-center justify-center rounded border border-dashed border-[var(--color-border)] px-3 text-center text-xs text-[var(--color-muted)]"
          style={{ height: `${height}px`, marginLeft: `${Y_GUTTER}px` }}
        >
          {chart.empty}
        </div>
      ) : (
        <div className="mt-1 flex items-stretch">
          <div className="relative shrink-0" style={{ width: `${Y_GUTTER}px`, height: `${height}px` }}>
            {chart.ticks.map((tick) => (
              <span
                key={`${tick.at}-${tick.label}`}
                className="num absolute right-1 -translate-y-1/2 text-[10px] text-[var(--color-muted)]"
                style={{ top: `${(tick.at / height) * 100}%` }}
              >
                {tick.label}
              </span>
            ))}
          </div>
          {/* Курсор слушает ОБЁРТКА, а не холст: точки ордеров лежат над
              холстом, и под ними перекрестие замирало бы ровно там, где
              сверяют ордер с ценой. */}
          <div className="relative min-w-0 flex-1" onMouseMove={move} onMouseLeave={leave}>
          <svg
            viewBox={`0 0 ${PLOT_W} ${height}`}
            preserveAspectRatio="none"
            className="block w-full"
            style={{ height: `${height}px` }}
            role="img"
            aria-label={title}
          >
            {/* Вертикальная сетка по делениям оси: ось зафиксирована по длине
                окна, и без штрихов подпись «+3:00» не привязана ни к чему —
                линия занимает лишь ту часть холста, которую окно уже прожило, а
                остальное пусто. Штрихи и показывают, сколько ещё впереди. */}
            {ticks.map((tick) => (
              <line
                key={`grid-${tick.at}`}
                x1={tick.at}
                y1={0}
                x2={tick.at}
                y2={height}
                stroke="var(--color-border)"
                strokeWidth={1}
                strokeOpacity={0.5}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {/* Открытие окна — пунктиром поверх сетки: слева котировка живёт
                ещё без страйка, справа — со страйком, и это два разных рынка на
                одной линии. Без отметки скачок стакана на открытии читался бы
                как событие внутри окна. */}
            {openAt !== null && (
              <line
                x1={openAt}
                y1={0}
                x2={openAt}
                y2={height}
                stroke="var(--color-muted)"
                strokeWidth={1}
                strokeDasharray="4 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {zeroAt !== undefined && (
              <line
                x1={0}
                y1={zeroAt}
                x2={PLOT_W}
                y2={zeroAt}
                stroke="var(--color-border)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            )}
            {/* Столбики — ПОД перекрестием и линиями: они фон события, а не
                сама линия, и перекрывать их им нечем. */}
            {chart.bars.map((bar) => (
              <rect
                key={bar.key}
                x={bar.x}
                y={bar.y}
                width={bar.w}
                height={bar.h}
                fill={bar.color}
                fillOpacity={0.65}
              />
            ))}
            {/* Перекрестие — ПОСЛЕ сетки и ДО линий: сетка под ним, а линии
                поверх, иначе курсор перечёркивал бы ту самую точку, значение
                которой показывает. */}
            {hoverX !== null && (
              <line
                x1={hoverX}
                y1={0}
                x2={hoverX}
                y2={height}
                stroke="var(--color-fg)"
                strokeWidth={1}
                strokeOpacity={0.35}
                vectorEffect="non-scaling-stroke"
              />
            )}
            {chart.lines.flatMap((l) =>
              l.paths.map((d, i) => (
                <path
                  key={`${l.key}-${i}`}
                  d={d}
                  fill="none"
                  stroke={l.color}
                  strokeWidth={l.faint ? 1 : 1.5}
                  strokeDasharray={l.faint ? "3 3" : undefined}
                  // Толщина в ЭКРАННЫХ пикселях: без этого растяжение по ширине
                  // раздавило бы линию по вертикали в волосок.
                  vectorEffect="non-scaling-stroke"
                />
              )),
            )}
          </svg>
          {/* Точки — НАД холстом отдельными узлами, а не кругами внутри SVG:
              холст растянут по ширине (preserveAspectRatio="none"), и круг в нём
              стал бы эллипсом, сплющенным тем сильнее, чем шире карточка. */}
          {marks.map((m) => (
            <OrderMark key={m.key} mark={m} hot={nearKeys.includes(m.key)} />
          ))}
          {near.length > 0 && <MarkTip marks={near} height={height} />}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Легенда: имя серии, её цвет и значение.
 *
 * Под курсором она показывает значение В ЭТОЙ СЕКУНДЕ, без курсора — последнее
 * известное. Подписи и цвета при этом не двигаются: подменяются только числа,
 * поэтому ни высота карточки, ни положение подписей от курсора не зависят.
 * Всплывающее окно пришлось бы позиционировать и переворачивать у края холста, а
 * читалось бы оно хуже — числа стояли бы не там, где стоят всегда.
 */
function Legend({ lines, hover }: { lines: TimelineLine[]; hover: TimelineHover | null }) {
  if (lines.length === 0) return null;
  return (
    <span className="flex flex-wrap items-baseline gap-3 text-[10px]">
      {lines.map((l) => (
        <span key={l.key} className="flex items-baseline gap-1">
          {l.swatch ? (
            <span
              aria-hidden
              className="inline-block h-2 w-2 self-center rounded-full"
              style={
                l.swatch === "dot"
                  ? { background: l.color }
                  : { border: `1.5px solid ${l.color}` }
              }
            />
          ) : (
            <span
              aria-hidden
              className="inline-block h-0.5 w-3 align-middle"
              style={{ background: l.color, opacity: l.faint ? 0.6 : 1 }}
            />
          )}
          <span className="text-[var(--color-muted)]">{l.label}</span>
          {/* Пустое значение НЕ печатается: у столбика нет «последнего», и «—»
              читалось бы как «ставок не было за всё окно». Под курсором оно
              появляется — там есть корзина, у которой значение есть. */}
          {(hover?.values[l.key] ?? l.value) !== "" && (
            <span className="num">{hover?.values[l.key] ?? l.value}</span>
          )}
        </span>
      ))}
    </span>
  );
}

/** Поперечник точки ордера, пиксели. */
const MARK_D = 10;

/**
 * Точка ордера.
 *
 * Сплошная — настоящий, кольцо — пробный: вид ордера читается формой, потому
 * что цвет уже занят исходом. Тёмная обводка у сплошной точки — не украшение:
 * без неё зелёная точка на зелёной линии стакана сливалась бы с линией.
 */
function OrderMark({ mark, hot }: { mark: TimelineMark; hot: boolean }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute rounded-full"
      style={{
        left: `${(mark.x / PLOT_W) * 100}%`,
        top: `${mark.y}px`,
        width: `${MARK_D}px`,
        height: `${MARK_D}px`,
        transform: "translate(-50%, -50%)",
        background: mark.paper ? "var(--color-panel)" : mark.color,
        border: mark.paper ? `2px solid ${mark.color}` : "1.5px solid var(--color-panel)",
        boxShadow: hot ? "0 0 0 1.5px var(--color-fg)" : undefined,
        opacity: mark.dim && !hot ? 0.45 : 1,
      }}
    />
  );
}

/** Сколько ордеров подсказка раскрывает целиком; остальные — числом. */
const TIP_MAX = 4;

/**
 * Подсказка по ордерам под курсором.
 *
 * Всплывающим окном, вопреки правилу легенды «числа стоят там, где стоят
 * всегда»: у ордера не одно число, а десяток полей, и места в легенде для них
 * нет. Окно отворачивает от края холста — к центру по горизонтали и от
 * ближайшего края по вертикали, — иначе у ордера в последние секунды окна оно
 * уезжало бы за карточку.
 */
function MarkTip({ marks, height }: { marks: TimelineMark[]; height: number }) {
  const anchor = marks[0];
  if (!anchor) return null;
  // По времени, а не по расстоянию: пачку ордеров читают в том порядке, в
  // каком они уходили.
  const shown = [...marks].sort((a, b) => a.x - b.x).slice(0, TIP_MAX);
  const left = anchor.x > PLOT_W * 0.6;
  const below = anchor.y < height / 2;
  return (
    <div
      className="pointer-events-none absolute z-20 w-max max-w-[320px] rounded border border-[var(--color-border)] bg-[var(--color-panel)] px-2 py-1.5 text-[11px] shadow-lg"
      style={{
        left: `${(anchor.x / PLOT_W) * 100}%`,
        top: `${anchor.y}px`,
        transform: `translate(${left ? "calc(-100% - 10px)" : "10px"}, ${
          below ? "8px" : "calc(-100% - 8px)"
        })`,
      }}
    >
      {shown.map((m, i) => (
        <div key={m.key} className={i > 0 ? "mt-1.5 border-t border-[var(--color-border)] pt-1.5" : ""}>
          <div className="flex items-center gap-1.5 font-medium">
            <span
              aria-hidden
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={
                m.paper ? { border: `1.5px solid ${m.color}` } : { background: m.color }
              }
            />
            {m.title}
          </div>
          {m.lines.map((text, j) => (
            <div key={j} className="num text-[var(--color-muted)]">
              {text}
            </div>
          ))}
        </div>
      ))}
      {marks.length > shown.length && (
        <div className="mt-1.5 text-[var(--color-muted)]">
          и ещё {marks.length - shown.length} рядом
        </div>
      )}
    </div>
  );
}

/** Координата перекрестия. null — курсора нет. */
function hoverX(at: number | null, from: number, span: number): number | null {
  if (at === null || span <= 0) return null;
  return (Math.max(0, Math.min(span, at - from)) / span) * PLOT_W;
}

